/**
 * Toast e diálogo de confirmação — os dois EL-166/EL-169 da v67, portados para módulo.
 *
 * Motivo de existirem: a v67 substituiu os `confirm()` do navegador porque a caixa nativa
 * não tem o tema, não diz o que vai acontecer e trava a página. A casca nasce com o
 * mesmo padrão para não reintroduzir `confirm()` pela porta dos fundos.
 */
import { h, limpar } from '../dom';
import { icone } from '../icones';

export type Tom = 'ok' | 'erro' | 'aviso' | 'info';

export interface OpcoesAviso {
  tom?: Tom;
  /**
   * Oferece "Desfazer". Quem passa isto está prestes a destruir algo, e a alternativa a
   * um desfazer é um diálogo de confirmação a cada exclusão — o simulador escolheu o
   * desfazer, e é comportamento que o porte não pode perder.
   */
  desfazer?: () => void;
}

let noToast: HTMLElement | null = null;
let cronometro: number | null = null;

function toastEl(): HTMLElement {
  if (noToast === null) {
    noToast = h('div', { class: 'toast' });
    document.body.appendChild(noToast);
  }
  return noToast;
}

function esconder(el: HTMLElement): void {
  el.classList.remove('show');
}

/**
 * Mensagem passageira no rodapé. É o ÚNICO toast do aplicativo — o simulador portado
 * também passa por aqui, em vez de manter o dele.
 *
 * Três regras herdadas do simulador, e cada uma tem motivo:
 *   · **erro fica até ser fechado.** Uma mensagem de erro que some sozinha é uma
 *     mensagem que o usuário não leu.
 *   · **com "Desfazer", 5,5 s** em vez de 2,6 s — tempo de perceber que errou.
 *   · **`info` é o tom neutro**, sem cor semântica.
 */
export function avisar(mensagem: string, tomOuOpcoes: Tom | OpcoesAviso = 'ok'): void {
  const o: OpcoesAviso = typeof tomOuOpcoes === 'string' ? { tom: tomOuOpcoes } : tomOuOpcoes;
  const tom: Tom = o.tom ?? 'info';

  const el = toastEl();
  el.className = 'toast' + (tom === 'info' ? '' : ` ${tom}`);
  limpar(el);

  const marca = tom === 'ok' ? icone.ok(15) : tom === 'erro' ? icone.alerta(15) : icone.info(15);
  el.appendChild(h('span', { class: 'tico', html: marca }));
  el.appendChild(h('span', { class: 'tmsg' }, mensagem));

  if (o.desfazer !== undefined) {
    el.appendChild(h('button', {
      class: 'undo',
      onclick: () => {
        o.desfazer?.();
        esconder(el);
      },
    }, 'Desfazer'));
  }
  /* 09/10/2026 (item 19): o AVISO longo fica o tempo de ser lido (~55 ms por caractere, de 2,6 s
     a 10 s). "48 vagas importadas — 34 vínculos mantidos; 2 desfeitos…" em 2,6 s some antes do
     fim da frase, e é a única notícia de que um vínculo saiu. `ok` e `info` seguem em 2,6 s. */
  const ms = o.desfazer !== undefined ? 5500
    : tom === 'aviso' ? Math.min(10000, Math.max(2600, mensagem.length * 55)) : 2600;
  /* Revisão do lote 1 (10/10/2026): o que fica mais que o padrão ganha o ✕, como o erro — por até
     10 s ele cobre o rodapé (e segura o clique); quem já leu tem de poder tirá-lo da frente. */
  if (tom === 'erro' || (tom === 'aviso' && ms > 2600)) {
    el.appendChild(h('button', { class: 'tx', title: 'Fechar', onclick: () => esconder(el) }, '✕'));
  }

  el.classList.add('show');
  if (cronometro !== null) clearTimeout(cronometro);
  if (tom !== 'erro') cronometro = window.setTimeout(() => esconder(el), ms);
}

/* ============================================================================
 * Confirmação
 * ========================================================================== */

export interface OpcoesConfirmar {
  titulo: string;
  /** Texto puro, ou nós prontos (`[h('b', {}, nome), ' …']`) quando precisa de negrito com dado do banco. */
  corpo: string | Node | readonly (Node | string)[];
  confirmar?: string;
  cancelar?: string;
  perigo?: boolean;
  /**
   * Trata `corpo` como marcação em vez de texto. **Só para corpo montado no código.**
   * O simulador usa negrito no nome do que vai ser apagado, e o padrão continua sendo
   * texto puro justamente para que interpolar dado do banco aqui exija uma decisão
   * explícita de quem escreve, e não passe por descuido.
   */
  corpoEhHtml?: boolean;
  /**
   * Abre com o foco em "Cancelar" em vez do botão de confirmar. Para a pergunta que nasce de um
   * gesto de DIGITAÇÃO (Tab/Enter numa célula): quem segue digitando como numa planilha não pode
   * confirmar sem querer uma ação irreversível — Enter, Enter segurado ou "Tab, número, Enter"
   * caíam no "Continuar e apagar" (revisão do lote 2, 10/10/2026, itens 24/25).
   */
  focarCancelar?: boolean;
  /**
   * Os dois botões são duas ESCOLHAS com rótulos longos (lote 4 de 10/10/2026, item 6: "Manter o que digitei
   * (R$ X)" × "Usar o oficial (R$ Y)"), não "Cancelar/Confirmar": empilhados, na largura do diálogo — lado a lado
   * eles não cabem e o da esquerda saía cortado.
   */
  escolha?: boolean;
  /**
   * Esc, ✕ e clique fora resolvem como o botão de CONFIRMAR — que é o caminho padrão (com o foco e o destaque).
   * Revisão do lote 4 (10/10/2026): no diálogo do LGC com rascunho antigo, o padrão é "Usar o oficial", e ele tem
   * de ser também o botão em destaque — não o outro.
   */
  padraoConfirmar?: boolean;
}

/** Resolve `true` só no botão de confirmar. Esc, clique fora e ✕ resolvem `false` (ou `true`, com `padraoConfirmar`). */
export function confirmar(o: OpcoesConfirmar): Promise<boolean> {
  return new Promise((resolve) => {
    let fechado = false;
    const fechar = (r: boolean) => {
      if (fechado) return;
      fechado = true;
      document.removeEventListener('keydown', aoTeclar);
      fundo.remove();
      resolve(r);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar(o.padraoConfirmar === true);
    };

    const botaoOk = h(
      'button',
      { class: `btn ${o.perigo === true ? 'danger solid' : 'pri'}`, onclick: () => fechar(true) },
      o.confirmar ?? 'Confirmar',
    );

    const botaoCancelar = h('button', { class: 'btn out', onclick: () => fechar(false) }, o.cancelar ?? 'Cancelar');

    const caixa = h(
      'div',
      { class: `modal cfm${o.perigo === true ? ' danger' : ''}${o.escolha === true ? ' cfm-escolha' : ''}`, 'data-el': 'EL-169' },
      h(
        'div',
        { class: 'mh' },
        h('span', { class: 'cfm-ic', html: o.perigo === true ? icone.alerta(18) : icone.info(18) }),
        h('div', {}, h('h3', {}, o.titulo)),
        h('button', { class: 'iconbtn', style: 'margin-left:auto', title: 'Fechar', onclick: () => fechar(o.padraoConfirmar === true) }, '✕'),
      ),
      o.corpoEhHtml === true && typeof o.corpo === 'string'
        ? h('div', { class: 'cfm-bd', html: o.corpo })
        : h('div', { class: 'cfm-bd' }, ...(typeof o.corpo === 'string' || o.corpo instanceof Node ? [o.corpo] : o.corpo)),
      h(
        'div',
        { class: 'cfm-ft' },
        botaoCancelar,
        botaoOk,
      ),
    );

    const fundo = h(
      'div',
      {
        class: 'ov show',
        onclick: (e: Event) => {
          if (e.target === fundo) fechar(o.padraoConfirmar === true);
        },
      },
      caixa,
    );

    document.body.appendChild(fundo);
    document.addEventListener('keydown', aoTeclar);
    (o.focarCancelar === true ? botaoCancelar : botaoOk).focus();
  });
}

/* ============================================================================
 * Pergunta de uma linha (nome de empreendimento, de cenário, …)
 * ========================================================================== */

export interface OpcoesPerguntar {
  titulo: string;
  rotulo: string;
  valor?: string;
  dica?: string;
  confirmar?: string;
}

/** Resolve com o texto informado, ou `null` se o usuário desistiu. */
export function perguntar(o: OpcoesPerguntar): Promise<string | null> {
  return new Promise((resolve) => {
    let fechado = false;
    const fechar = (r: string | null) => {
      if (fechado) return;
      fechado = true;
      document.removeEventListener('keydown', aoTeclar);
      fundo.remove();
      resolve(r);
    };
    const confirmarAgora = () => {
      const v = campo.value.trim();
      if (v === '') {
        campo.classList.add('missing');
        campo.focus();
        return;
      }
      fechar(v);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar(null);
    };

    const campo = h('input', {
      class: 'input',
      value: o.valor ?? '',
      placeholder: o.dica ?? '',
      oninput: () => campo.classList.remove('missing'),
      onkeydown: (e: Event) => {
        if ((e as KeyboardEvent).key === 'Enter') confirmarAgora();
      },
    });

    const caixa = h(
      'div',
      { class: 'modal cfm' },
      h('div', { class: 'mh' }, h('div', {}, h('h3', {}, o.titulo)),
        h('button', { class: 'iconbtn', style: 'margin-left:auto', title: 'Fechar', onclick: () => fechar(null) }, '✕')),
      h('div', { class: 'cfm-bd' }, h('div', { class: 'field' }, h('label', { class: 'lbl' }, o.rotulo), campo)),
      h('div', { class: 'cfm-ft' },
        h('button', { class: 'btn out', onclick: () => fechar(null) }, 'Cancelar'),
        h('button', { class: 'btn pri', onclick: confirmarAgora }, o.confirmar ?? 'Criar')),
    );

    const fundo = h('div', {
      class: 'ov show',
      onclick: (e: Event) => {
        if (e.target === fundo) fechar(null);
      },
    }, caixa);

    document.body.appendChild(fundo);
    document.addEventListener('keydown', aoTeclar);
    campo.focus();
    campo.select();
  });
}

/* ============================================================================
 * Formulário de vários campos
 *
 * `perguntar` acima resolve UM campo, e tem sete chamadores — fica como está. Este
 * irmão existe porque editar cadastro precisa de dois ou mais campos ao mesmo
 * tempo, e a alternativa era o que já foi feito duas vezes na tela de equipe:
 * montar o modal à mão, de novo, com validação própria.
 * ========================================================================== */

export interface CampoFormulario {
  chave: string;
  rotulo: string;
  valor?: string;
  dica?: string;
  /** Vazio não fecha o diálogo — o campo fica marcado e recebe o foco. */
  obrigatorio?: boolean;
  ajuda?: string;
}

export interface OpcoesFormulario {
  titulo: string;
  campos: CampoFormulario[];
  confirmar?: string;
  descricao?: string;
}

/** Resolve com {chave: valor} já aparado, ou `null` se o usuário desistiu. */
export function formulario(o: OpcoesFormulario): Promise<Record<string, string> | null> {
  return new Promise((resolve) => {
    let fechado = false;
    const fechar = (r: Record<string, string> | null) => {
      if (fechado) return;
      fechado = true;
      document.removeEventListener('keydown', aoTeclar);
      fundo.remove();
      resolve(r);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar(null);
    };

    const entradas = new Map<string, HTMLInputElement>();
    const confirmarAgora = () => {
      let faltou: HTMLInputElement | null = null;
      const saida: Record<string, string> = {};
      for (const c of o.campos) {
        const el = entradas.get(c.chave);
        if (el === undefined) continue;
        const v = el.value.trim();
        if (c.obrigatorio === true && v === '') {
          el.classList.add('missing');
          if (faltou === null) faltou = el;
        }
        saida[c.chave] = v;
      }
      if (faltou !== null) {
        faltou.focus();
        return;
      }
      fechar(saida);
    };

    const campos = o.campos.map((c) => {
      const el = h('input', {
        class: 'input',
        value: c.valor ?? '',
        placeholder: c.dica ?? '',
        oninput: () => el.classList.remove('missing'),
        onkeydown: (e: Event) => {
          if ((e as KeyboardEvent).key === 'Enter') confirmarAgora();
        },
      }) as HTMLInputElement;
      entradas.set(c.chave, el);
      const bloco = h('div', { class: 'field' }, h('label', { class: 'lbl' }, c.rotulo), el);
      if (c.ajuda !== undefined) {
        bloco.appendChild(h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' }, c.ajuda));
      }
      return bloco;
    });

    const corpo = h('div', { class: 'cfm-bd', style: 'display:grid;gap:var(--sp-3)' }, ...campos);
    if (o.descricao !== undefined) {
      corpo.insertBefore(h('span', { class: 'muted', style: 'font-size:var(--fs-xs)' }, o.descricao), corpo.firstChild);
    }

    const caixa = h(
      'div',
      { class: 'modal cfm', 'data-el': 'EL-187' },
      h('div', { class: 'mh' }, h('div', {}, h('h3', {}, o.titulo)),
        h('button', { class: 'iconbtn', style: 'margin-left:auto', title: 'Fechar', onclick: () => fechar(null) }, '✕')),
      corpo,
      h('div', { class: 'cfm-ft' },
        h('button', { class: 'btn out', onclick: () => fechar(null) }, 'Cancelar'),
        h('button', { class: 'btn pri', onclick: confirmarAgora }, o.confirmar ?? 'Salvar')),
    );

    const fundo = h('div', {
      class: 'ov show',
      onclick: (e: Event) => {
        if (e.target === fundo) fechar(null);
      },
    }, caixa);

    document.body.appendChild(fundo);
    document.addEventListener('keydown', aoTeclar);
    const primeiro = entradas.get(o.campos[0]?.chave ?? '');
    primeiro?.focus();
    primeiro?.select();
  });
}
